import React, { useState, useEffect, useRef } from 'react';
import { Link } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  OkrTreeNode,
  GroupLead,
  OkrObjective,
  formatQuarter,
  ownerGroupSlug,
  formatGroupName,
} from '../../hooks/useOkrEntities';
import { PersonRow } from '../PersonRow';

const KR_BADGE: Record<string, string> = {
  green: '#147f42',
  yellow: '#7a5500',
  red: '#9e1a10',
};

function objProgress(obj: OkrObjective): number {
  const krs = obj.keyResults.filter(kr => kr.progress !== undefined);
  if (!krs.length) return 0;
  return Math.round(krs.reduce((s, kr) => s + (kr.progress ?? 0), 0) / krs.length);
}

const useStyles = makeStyles(theme => ({
  '@keyframes cardFromRight': {
    '0%':   { transform: 'translateX(64px)', opacity: 0.3 },
    '100%': { transform: 'translateX(0)',    opacity: 1   },
  },
  '@keyframes cardFromLeft': {
    '0%':   { transform: 'translateX(-64px)', opacity: 0.3 },
    '100%': { transform: 'translateX(0)',     opacity: 1   },
  },
  slideFromRight: {
    animation: '$cardFromRight 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  slideFromLeft: {
    animation: '$cardFromLeft 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  card: {
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'box-shadow 0.2s, opacity 0.2s',
    display: 'flex',
    flexDirection: 'column' as const,
    boxSizing: 'border-box' as const,
    borderRadius: 12,
  },
  selected: {
    boxShadow: '0 4px 20px rgba(95,1,111,0.18)',
    border: '2px solid #5f016f',
  },
  // Applied to the outer wrapper so ghost cards dim together with the front card
  dimmedWrapper: {
    opacity: 0.45,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(1),
  },
  verticalChip: {
    display: 'inline-block',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '1px 6px',
  },
  groupName: {
    fontWeight: 600,
    fontSize: '1.1rem',
    lineHeight: 1.25,
  },
  nameRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    marginBottom: theme.spacing(0.75),
  },
  objTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    lineHeight: 1.35,
    marginBottom: theme.spacing(1.5),
    flexGrow: 1,
  },
  quarterPill: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#f0e6f4',
    color: '#5f016f',
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 10,
    fontWeight: 600,
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.6px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
  },
  progressRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 3,
  },
  bar: {
    height: 4,
    borderRadius: 2,
    marginBottom: theme.spacing(1.5),
    backgroundColor: '#e0e0e0',
  },
  krHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    cursor: 'pointer',
  },
  krItem: {
    marginBottom: theme.spacing(1.25),
  },
  krTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 4,
  },
  krBadge: {
    fontSize: 9,
    fontWeight: 700,
    color: '#fff',
    borderRadius: 3,
    padding: '1px 5px',
    flexShrink: 0,
    marginTop: 2,
    letterSpacing: '0.3px',
  },
  krTitle: {
    fontSize: '0.78rem',
    lineHeight: 1.35,
    flex: 1,
    minWidth: 0,
  },
  krValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginLeft: 30,
    marginBottom: 4,
  },
  krValue: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic' as const,
    flex: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
  },
  krBar: {
    height: 3,
    borderRadius: 2,
    backgroundColor: '#e0e0e0',
    marginLeft: 30,
  },
  divider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(1, 0),
  },
  objNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.75),
  },
  objNavLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    textAlign: 'center' as const,
    flex: 1,
  },
}));

interface VerticalCardProps {
  node: OkrTreeNode;
  selected: boolean;
  onClick: () => void;
  groupLead?: GroupLead;
  chipLabel?: string;
  /** When false, no card in the carousel is selected — don't dim this card */
  anySelected?: boolean;
  /** Called with the active objective index whenever this card is selected or the user navigates objectives within it */
  onObjChange?: (index: number) => void;
}


export function VerticalCard({ node, selected, onClick, groupLead, chipLabel = 'Vertical', anySelected = true, onObjChange }: VerticalCardProps) {
  const classes = useStyles();
  const theme = useTheme();
  const [selectedObjIndex, setSelectedObjIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [slideDir, setSlideDir] = useState<'left' | 'right' | null>(null);
  const [krsExpanded, setKrsExpanded] = useState(false);
  const slideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!selected) setKrsExpanded(false);
  }, [selected]);
  useEffect(() => {
    if (selected) onObjChange?.(selectedObjIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, selectedObjIndex]);

  const { entity } = node;
  const groupName = formatGroupName(ownerGroupSlug(entity));
  const objectives = entity.spec.objectives;
  const currentObj = objectives[selectedObjIndex] ?? objectives[0];
  const ownPrep = objProgress(currentObj);

  // Number of ghost cards to render behind (cap at 2)
  const ghostCount = Math.min(objectives.length - 1, 2);
  // Ghost cards use a lightly tinted background so they're distinguishable from the front card
  let ghostBg = theme.palette.type === 'dark' ? '#2d2d2d' : '#f0f0f0';
  if (selected) ghostBg = '#f3e8f6';

  let slideClass: string | undefined;
  if (slideDir === 'right') slideClass = classes.slideFromRight;
  else if (slideDir === 'left') slideClass = classes.slideFromLeft;
  const ghostBorder = selected ? '1.5px solid rgba(95,1,111,0.3)' : `1.5px solid ${theme.palette.divider}`;

  function navigateObj(delta: number, e: React.MouseEvent) {
    e.stopPropagation();
    const next = (selectedObjIndex + delta + objectives.length) % objectives.length;
    if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
    setSlideDir(delta > 0 ? 'left' : 'right');
    setSelectedObjIndex(next);
    setAnimKey(k => k + 1);
    slideTimerRef.current = setTimeout(() => setSlideDir(null), 470);
  }

  return (
    // Wrapper dims the whole stack (front card + ghost cards) together; position:relative anchors ghost absolutes
    <Box
      className={anySelected && !selected ? classes.dimmedWrapper : undefined}
      style={{ position: 'relative', marginTop: 12, marginRight: 12 }}
    >
      {/* Ghost cards peek from the RIGHT — stacked deck effect, decorative only */}
      {ghostCount >= 2 && (
        <Box
          style={{
            position: 'absolute',
            top: -12,
            left: 12,
            right: -12,
            bottom: 12,
            borderRadius: 12,
            backgroundColor: ghostBg,
            border: ghostBorder,
            zIndex: 1,
          }}
        />
      )}
      {ghostCount >= 1 && (
        <Box
          style={{
            position: 'absolute',
            top: -6,
            left: 6,
            right: -6,
            bottom: 6,
            borderRadius: 12,
            backgroundColor: ghostBg,
            border: ghostBorder,
            zIndex: 2,
          }}
        />
      )}
    {/* Whole-card slide wrapper — remounted via animKey on each navigation to restart the animation */}
    <Box
      key={animKey}
      className={slideClass}
      style={{ overflow: 'hidden', position: 'relative', zIndex: 3, backgroundColor: theme.palette.background.paper, borderRadius: 12 }}
    >
    <Paper
      className={`${classes.card} ${selected ? classes.selected : ''}`}
      elevation={selected ? 3 : 1}
      onClick={onClick}
    >
      {/* Header: chip left, person right */}
      <Box className={classes.cardHeader}>
        <span className={classes.verticalChip}>{chipLabel}</span>
        {groupLead && <PersonRow accountable={groupLead} avatarSize={22} nameStyle={{ fontSize: 10, fontWeight: 600, color: theme.palette.text.secondary }} />}
      </Box>

      {/* Group name centred, quarter pill right-aligned */}
      <Box className={classes.nameRow}>
        <span />
        <Link
          to={`/catalog/${entity.metadata.namespace ?? 'default'}/group/${ownerGroupSlug(entity)}`}
          style={{ textDecoration: 'none', color: 'inherit', textAlign: 'center' }}
          onClick={e => e.stopPropagation()}
        >
          <Typography className={classes.groupName}>{groupName}</Typography>
        </Link>
        <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box component="span" className={classes.quarterPill}>
            {formatQuarter(entity.spec.quarter)}
          </Box>
        </Box>
      </Box>

      {/* Objective navigator */}
      {objectives.length > 1 && (
        <Box className={classes.objNav} onClick={e => e.stopPropagation()}>
          <IconButton
            size="small"
            onClick={e => navigateObj(-1, e)}
            style={{ padding: 2 }}
          >
            <ChevronLeftIcon style={{ fontSize: 15 }} />
          </IconButton>
          <Typography className={classes.objNavLabel}>
            Objective {selectedObjIndex + 1} / {objectives.length}
          </Typography>
          <IconButton
            size="small"
            onClick={e => navigateObj(1, e)}
            style={{ padding: 2 }}
          >
            <ChevronRightIcon style={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      )}

      {/* Objective title */}
      {currentObj && (
        <Typography className={classes.objTitle}>{currentObj.title}</Typography>
      )}

      {/* PROGRESS */}
      {ownPrep > 0 && (
        <>
          <Box className={classes.progressRow}>
            <Typography className={classes.sectionLabel}>Progress</Typography>
            <Typography variant="caption" style={{ fontWeight: 700, color: '#5f016f' }}>
              {ownPrep}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={ownPrep} className={classes.bar} />
        </>
      )}

      {/* KEY RESULTS */}
      {currentObj && (
        <>
          <Box
            className={classes.krHeader}
            onClick={e => { e.stopPropagation(); setKrsExpanded(v => !v); }}
          >
            <Typography className={classes.sectionLabel}>
              Key Results · {currentObj.keyResults.length} KRs
            </Typography>
            <Box display="flex" alignItems="center">
              {krsExpanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
            </Box>
          </Box>

          <Collapse in={krsExpanded}>
            <Box>
              {currentObj.keyResults.map((kr, i) => (
                <Box key={i} className={classes.krItem}>
                  <Box className={classes.krTitleRow}>
                    <span
                      className={classes.krBadge}
                      style={{ backgroundColor: KR_BADGE[kr.confidence] ?? '#555' }}
                    >
                      KR{i + 1}
                    </span>
                    <Typography className={classes.krTitle}>{kr.title}</Typography>
                    {kr.progress !== undefined && (
                      <Typography variant="caption" style={{ fontWeight: 700, color: '#5f016f', whiteSpace: 'nowrap' as const, flexShrink: 0 }}>
                        {kr.progress}%
                      </Typography>
                    )}
                  </Box>
                  {kr.progress !== undefined && (
                    <LinearProgress
                      variant="determinate"
                      value={kr.progress}
                      className={classes.krBar}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}


    </Paper>
    </Box>
    </Box>
  );
}
